/**
 * The IDE snapshot that rides with every run (`ide_state` on POST /api/runs).
 *
 * A MODULE-LEVEL SNAPSHOT, NOT A PROP. `createRun` is called from five unrelated
 * places — the composer, follow-up chips, the IDE send path, context compression,
 * the session hook — and none of them has any business knowing about Git. Threading
 * a repo name through all five to reach one request body is the kind of plumbing
 * that rots the moment a sixth caller appears. The Weave route keeps this current;
 * `createRun` reads it on the way past.
 *
 * Everything here is a HINT. The run is valid without it, and the backend treats
 * each payload as the freshest snapshot (last write wins within the session).
 */
export type MultiAgentIdeState = {
  /** "owner/repo", as selected in Git & pipelines. */
  git_repo?: string;
  /** The working branch chosen there, else the repo's default. */
  git_branch?: string;
};

let current: MultiAgentIdeState = {};

export function setMultiAgentIdeState(next: MultiAgentIdeState): void {
  current = next;
}

/**
 * The snapshot, or null when there is nothing worth sending.
 *
 * A BRANCH WITHOUT ITS REPO IS NEVER SENT. The two come from separate queries with
 * separate cache lifetimes, so a message sent while the repo is still in flight used
 * to carry `{git_branch}` alone — and a half-answer was worse than none: the run was
 * told a branch existed, went looking for the repository it belonged to, picked one
 * of the account's 27 by name, and pushed to it (dev, 2026-09-23). Either both, or
 * nothing and the backend asks.
 */
export function multiAgentIdeState(): MultiAgentIdeState | null {
  const repo = (current.git_repo || "").trim();
  if (!repo) return null;
  const branch = (current.git_branch || "").trim();
  return branch ? { git_repo: repo, git_branch: branch } : { git_repo: repo };
}

/**
 * The snapshot a LANE V2 turn rides with: the branch goes even when no repository is known.
 *
 * WHY THIS ONE MAY SEND A BRANCH ALONE (the old-lane rule above forbids it). The incident that rule guards was a lone branch
 * steering an MCP push at a repository the agent then guessed. On Lane V2 the branch has a different job: it is the workspace
 * branch a chat "save it" saves the finished change to (`ide_state.git_branch`, no-deploy program). MSA registers a Git
 * selection for connector tools only when `git_repo` is present (`tools/mcp/git_selection.py::register`), so a branch without
 * a repo cannot steer a push; it only names where "save it" goes. Without it the seat answers "name the branch" instead.
 *
 * Null when there is neither a repository nor a branch.
 */
export function laneV2IdeState(): MultiAgentIdeState | null {
  const repo = (current.git_repo || "").trim();
  const branch = (current.git_branch || "").trim();
  if (!repo && !branch) return null;
  return { ...(repo ? { git_repo: repo.slice(0, 200) } : {}), ...(branch ? { git_branch: branch.slice(0, 255) } : {}) };
}
